import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
    canSubmitTemplateUpload,
    EA_WASTE_RETURN_MAX_UPLOAD_BYTES,
    EA_WASTE_RETURN_TEMPLATE_REPLACE_WARNING,
    formatFileSize,
    shouldWarnBeforeTemplateReplace,
    validateTemplateUploadFile,
} from "./wasteReturnTemplateMappingDefaults";

export interface ReplaceTemplateModalProps {
    show: boolean;
    hasActiveTemplate: boolean;
    uploading: boolean;
    uploadError: string | null;
    onHide: () => void;
    onUpload: (file: File, name: string) => Promise<boolean>;
}

export function ReplaceTemplateModal({
    show,
    hasActiveTemplate,
    uploading,
    uploadError,
    onHide,
    onUpload,
}: ReplaceTemplateModalProps) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [templateName, setTemplateName] = useState("");
    const [confirmReplace, setConfirmReplace] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const showReplaceWarning = shouldWarnBeforeTemplateReplace(hasActiveTemplate);

    const resetForm = () => {
        setSelectedFile(null);
        setTemplateName("");
        setConfirmReplace(false);
        setFileError(null);
        setFileInputKey((key) => key + 1);
    };

    useEffect(() => {
        if (show) {
            resetForm();
        }
    }, [show]);

    const handleFileChange = (file: File | null) => {
        setSelectedFile(file);
        if (!file) {
            setFileError(null);
            return;
        }

        setFileError(validateTemplateUploadFile(file));
    };

    const handleClose = () => {
        if (uploading) return;
        onHide();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedFile) return;

        const validationError = validateTemplateUploadFile(selectedFile);
        if (validationError) {
            setFileError(validationError);
            return;
        }

        if (showReplaceWarning && !confirmReplace) return;

        const succeeded = await onUpload(selectedFile, templateName);
        if (succeeded) {
            resetForm();
            onHide();
        }
    };

    const canSubmit =
        canSubmitTemplateUpload({
            hasFile: !!selectedFile,
            showReplaceWarning,
            confirmReplace,
        }) &&
        !fileError &&
        !uploading;

    const title = hasActiveTemplate ? "Replace template" : "Upload template";
    const submitLabel = uploading ? "Uploading…" : hasActiveTemplate ? "Upload" : "Upload template";

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!uploading}>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {uploadError && <Alert variant="danger">{uploadError}</Alert>}

                    {showReplaceWarning && (
                        <Alert variant="warning">
                            {EA_WASTE_RETURN_TEMPLATE_REPLACE_WARNING}
                            <Form.Check
                                type="checkbox"
                                className="mt-2"
                                label="I understand this will replace the active template"
                                checked={confirmReplace}
                                onChange={(e) => setConfirmReplace(e.target.checked)}
                                disabled={uploading}
                            />
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Workbook file (.xlsx)</Form.Label>
                        <Form.Control
                            key={fileInputKey}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                            isInvalid={!!fileError}
                            disabled={uploading}
                        />
                        <Form.Text className="text-muted">
                            Maximum file size: {formatFileSize(EA_WASTE_RETURN_MAX_UPLOAD_BYTES)}.
                        </Form.Text>
                        {fileError && <Form.Control.Feedback type="invalid">{fileError}</Form.Control.Feedback>}
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Template name (optional)</Form.Label>
                        <Form.Control
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="e.g. EA 2026 template"
                            disabled={uploading}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={!canSubmit}>
                        {submitLabel}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
